<template>
  <label
    ref="droppable"
    v-dragndrop
    class="droppable"
    data-drop-class="droppable--hovered"
  >
    <input
      ref="droppableInput"
      class="input input--invisible"
      type="file"
      :name="name"
      :multiple="multiple"
      :accept="fileTypes"
    />

    <div v-show="!isProcessing" class="droppable__label">
      <slot name="label" />
    </div>

    <img v-if="hasPreview && imageSrc" class="img img--block" :src="imageSrc" />

    <div v-if="hasLoading" v-show="isProcessing" class="droppable__loading">
      <slot name="loading" />
    </div>

    <ElProgress
      v-if="hasProgress"
      v-show="isProcessing"
      class="droppable__progress"
      type="circle"
      :width="100"
      :percentage="percentage"
      :status="successful ? 'success' : undefined"
    />
  </label>
</template>

<script>
import dragndrop from '../../directives/dragndrop';
import { Upload } from '@/api/api';

export default {
  name: 'Droppable',

  directives: {
    dragndrop,
  },

  props: {
    name: {
      type: String,
      default: 'file',
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    fileTypes: {
      type: String,
      default: '',
    },

    uploadUrl: {
      type: String,
      required: true,
    },

    imageUrl: {
      // the url given from account component
      type: String,
      default: '',
    },

    hasLoading: {
      type: Boolean,
      default: false,
    },

    hasProgress: {
      type: Boolean,
      default: false,
    },

    hasPreview: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      percentage: 0,
      isProcessing: false,
      successful: false,
      // the src to be set on the image
      imageSrc: this.imageUrl,
    };
  },

  mounted() {
    this.$refs.droppableInput.addEventListener(
      'change',
      this.onChangeHandler,
      false,
    );
    this.$refs.droppable.addEventListener('drop', this.onChangeHandler, false);
  },

  beforeDestroy() {
    this.$refs.droppableInput.removeEventListener(
      'change',
      this.onChangeHandler,
      false,
    );
    this.$refs.droppable.removeEventListener(
      'drop',
      this.onChangeHandler,
      false,
    );
  },

  methods: {
    propagatePath(imgPath) {
      this.$emit('uploaded', imgPath);
    },

    uploadFile(data) {
      this.isProcessing = true;

      return Upload(this.uploadUrl, data, {
        // disabling the top progress bar for file upload
        showProgressBar: false,
        progress: e => {
          if (e.lengthComputable) {
            this.percentage = Math.round((e.loaded * 100) / e.total);
          }
        },
      })
        .then(({ body: { path, url } }) => {
          this.propagatePath(path);
          this.imageSrc = url;
          // change the state of the progressbar
          this.successful = true;
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .then(() => {
          this.successful = false;
          this.isProcessing = false;
        });
    },

    getFormData(files, name) {
      const form = new FormData();

      Array.from(files).forEach(file => {
        form.append(name, file);
      });

      return form;
    },

    onChangeHandler(e) {
      const files = e.target.files || e.dataTransfer.files;
      const formData = this.getFormData(files, this.name);
      this.uploadFile(formData);
    },
  },
};
</script>

<style lang="scss">
.droppable {
  position: relative;

  /* Box Model */
  display: block;
  min-height: $lb-droppable-min-height;
  border: $lb-droppable-border;

  cursor: pointer;

  &:active,
  &--hovered {
    border-color: $lb-droppable-active-border-color;
  }

  &__label {
    /* Positioning */
    position: absolute;
    top: 50%;

    /* Box Model*/
    width: 100%;

    /* Typography */
    color: $lb-droppable-label-color;
    text-align: center;

    /* Misc*/
    transform: translate(0, -50%);
  }

  &__progress {
    top: 50%;
    left: 50%;
    margin-top: -50px;
    margin-left: -50px;

    // overriding the element-ui default style
    &.el-progress--circle {
      position: absolute;
    }

    .el-progress-circle__path {
      transition: none !important;
    }
  }
}
</style>
